import { updateData } from "../Services/updateData";
import { endpoints } from "./UrlEnum";
import { Vocabulary } from "./Vocabulary";

/**
 * Check if uploading a new document completes all required documents
 * and update order status to "Documente" if needed
 *
 * @param orderId - The order ID
 * @param newDocumentTypeId - The document type ID being uploaded
 * @param requiredDocumentTypes - Array of required document type IDs
 * @param existingDocuments - Array of existing documents
 * @param allStatuses - Array of all available order statuses
 * @returns Promise<void>
 */
export async function checkAndUpdateOrderStatusOnDocumentUpload(
  orderId: string,
  newDocumentTypeId: string,
  requiredDocumentTypes: string[],
  existingDocuments: any[],
  allStatuses: any[]
): Promise<void> {
  if (!requiredDocumentTypes?.length || !allStatuses?.length) {
    return;
  }

  // Get existing document type IDs
  const existingDocTypeIds = existingDocuments.map(
    (doc: any) => (doc.documentTypeId as any)?._id || doc.documentTypeId
  );

  // Check if required documents were already complete BEFORE this upload
  const wasComplete = requiredDocumentTypes.every((reqTypeId: string) =>
    existingDocTypeIds.includes(reqTypeId)
  );

  // If already complete, no need to update status
  if (wasComplete) {
    return;
  }

  // Add the newly uploaded document type
  const allDocTypeIds = [...existingDocTypeIds, newDocumentTypeId];

  // Check if all required documents are now uploaded
  const isNowComplete = requiredDocumentTypes.every((reqTypeId: string) =>
    allDocTypeIds.includes(reqTypeId)
  );

  // Only update status if we just completed (wasn't complete before, but is now)
  if (isNowComplete) {
    await updateOrderStatusToDocuments(orderId, allStatuses);
  }
}

/**
 * Update order status to "Documente"
 *
 * @param orderId - The order ID
 * @param allStatuses - Array of all available statuses
 * @returns Promise<void>
 */
async function updateOrderStatusToDocuments(
  orderId: string,
  allStatuses: any[]
): Promise<void> {
  // Find the "Documente" status object
  const documentsStatus = allStatuses.find(
    (status: any) => status.name === Vocabulary.documents
  );

  if (!documentsStatus) {
    console.error('Status "Documente" not found in available statuses');
    return;
  }

  // Update order with status ID
  try {
    await updateData(`${endpoints.ordersEndpoint}/${orderId}`, {
      status: documentsStatus._id,
    });
  } catch (error) {
    console.error("Error updating order status to Documente:", error);
    throw error;
  }
}
